import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import client, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, Upload, Camera, AlertTriangle, CheckCircle2, RotateCw, Clock, Video, VideoOff } from "lucide-react";
import { estimatePose } from "../lib/poseMl";
import { makeCounter, getMlKey } from "../lib/repCounters";

function pickMimeType() {
    const candidates = [
        "video/mp4;codecs=h264,aac",
        "video/mp4",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
    ];
    for (const t of candidates) {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) {
            return t;
        }
    }
    return "";
}

export default function ChallengeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [phase, setPhase] = useState("intro"); // intro | active | upload
    const [seconds, setSeconds] = useState(0);
    const [running, setRunning] = useState(false);
    const [file, setFile] = useState(null);
    const [notes, setNotes] = useState("");
    const [progress, setProgress] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [cameraReady, setCameraReady] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [recording, setRecording] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [weightKg, setWeightKg] = useState("");
    const [repsDone, setRepsDone] = useState("");
    const [mlReps, setMlReps] = useState(0);
    const [mlDepth, setMlDepth] = useState(0);
    const [mlFormScore, setMlFormScore] = useState(1);
    const [mlLastValid, setMlLastValid] = useState(true);
    const [mlEnabled, setMlEnabled] = useState(false);
    const [mlReady, setMlReady] = useState(false);
    const [mlError, setMlError] = useState(null);
    const counterRef = useRef(null);
    const mlRafRef = useRef(null);
    const intervalRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const videoElRef = useRef(null);
    const mimeRef = useRef("");

    useEffect(() => {
        client.get(`/challenges/${id}`).then((r) => {
            setData(r.data);
            setSeconds(r.data.challenge.time_limit_seconds);
        });
    }, [id]);

    useEffect(() => {
        if (running) {
            intervalRef.current = setInterval(() => {
                setSeconds((s) => {
                    if (s <= 1) {
                        clearInterval(intervalRef.current);
                        setRunning(false);
                        return 0;
                    }
                    return s - 1;
                });
            }, 1000);
        } else if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [running]);

    // Manage blob preview URL with revoke on file change / unmount
    useEffect(() => {
        if (file instanceof File || file instanceof Blob) {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setPreviewUrl(null);
        return undefined;
    }, [file]);

    // ----- Camera lifecycle -----
    const stopStream = () => {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            try { recorderRef.current.stop(); } catch {}
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        recorderRef.current = null;
        setRecording(false);
        setCameraReady(false);
    };

    useEffect(() => {
        if (phase !== "active") return undefined;
        let cancelled = false;
        (async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
                    audio: true,
                });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                streamRef.current = stream;
                if (videoElRef.current) {
                    videoElRef.current.srcObject = stream;
                    videoElRef.current.play().catch(() => {});
                }
                mimeRef.current = pickMimeType();
                setCameraReady(true);
            } catch (e) {
                setCameraError(e?.message || "Camera permission denied");
            }
        })();
        return () => {
            cancelled = true;
            stopStream();
        };
        // eslint-disable-next-line
    }, [phase]);

    // ----- ML pose tracking loop (runs only when timer running + supported exercise) -----
    useEffect(() => {
        const mlKey = data ? getMlKey(data.challenge?.exercise_type) : null;
        if (!mlKey) {
            setMlEnabled(false);
            return undefined;
        }
        setMlEnabled(true);
        if (phase !== "active" || !cameraReady || !running) {
            // Pause the loop but keep counter state.
            if (mlRafRef.current) cancelAnimationFrame(mlRafRef.current);
            mlRafRef.current = null;
            return undefined;
        }
        if (!counterRef.current) {
            counterRef.current = makeCounter(mlKey);
            setMlReps(0);
            setMlDepth(0);
            setMlFormScore(1);
            setMlLastValid(true);
        }
        let cancelled = false;
        let lastInfer = 0;
        const TARGET_INTERVAL = 100; // ~10fps
        const tick = async (ts) => {
            if (cancelled) return;
            if (ts - lastInfer >= TARGET_INTERVAL) {
                lastInfer = ts;
                try {
                    const kps = await estimatePose(videoElRef.current);
                    if (kps && counterRef.current) {
                        counterRef.current.update(kps);
                        const c = counterRef.current;
                        setMlReps(c.count);
                        setMlDepth(c.depth);
                        setMlFormScore(c.formScore);
                        setMlLastValid(c.lastRepValid);
                    }
                    if (!mlReady) setMlReady(true);
                } catch (e) {
                    if (!mlError) setMlError(e?.message || "ML init failed");
                }
            }
            mlRafRef.current = requestAnimationFrame(tick);
        };
        mlRafRef.current = requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            if (mlRafRef.current) cancelAnimationFrame(mlRafRef.current);
            mlRafRef.current = null;
        };
        // eslint-disable-next-line
    }, [phase, cameraReady, running, data]);

    // Reset counter when leaving active phase
    useEffect(() => {
        if (phase !== "active") {
            counterRef.current = null;
            setMlReps(0);
            setMlDepth(0);
            setMlFormScore(1);
            setMlLastValid(true);
        }
    }, [phase]);

    // Auto start/stop MediaRecorder with the running state
    useEffect(() => {
        if (phase !== "active" || !cameraReady || !streamRef.current) return;
        if (running && !recorderRef.current) {
            try {
                const opts = mimeRef.current ? { mimeType: mimeRef.current } : undefined;
                const rec = new MediaRecorder(streamRef.current, opts);
                chunksRef.current = [];
                rec.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
                };
                rec.onstop = () => {
                    const type = mimeRef.current || "video/webm";
                    const blob = new Blob(chunksRef.current, { type });
                    const ext = type.includes("mp4") ? "mp4" : "webm";
                    const f = new File([blob], `proof-${Date.now()}.${ext}`, { type });
                    setFile(f);
                    setRecording(false);
                };
                rec.start(1000);
                recorderRef.current = rec;
                setRecording(true);
            } catch (e) {
                setCameraError("Recording failed: " + (e?.message || "unknown"));
            }
        }
        if (!running && recorderRef.current && recorderRef.current.state === "recording") {
            try { recorderRef.current.pause(); } catch {}
        } else if (running && recorderRef.current && recorderRef.current.state === "paused") {
            try { recorderRef.current.resume(); } catch {}
        }
    }, [running, cameraReady, phase]);

    // When seconds hit 0, finalize recording
    useEffect(() => {
        if (phase !== "active") return;
        if (seconds === 0 && recorderRef.current && recorderRef.current.state !== "inactive") {
            try { recorderRef.current.stop(); } catch {}
        }
    }, [seconds, phase]);

    const finalizeAndUpload = () => {
        // Stop directly (not pause→stop) for clean finalization
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            try { recorderRef.current.stop(); } catch {}
        }
        setRunning(false);
        // Give MediaRecorder's onstop a tick to flush chunks into the file
        setTimeout(() => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
            recorderRef.current = null;
            setRecording(false);
            setCameraReady(false);
            setPhase("upload");
        }, 400);
    };

    if (!data) return <div className="p-6 text-zinc-400">Loading...</div>;
    const c = data.challenge;
    const sub = data.latest_submission;

    const submit = async () => {
        if (!file) {
            toast.error("Pick or record a video first");
            return;
        }
        setSubmitting(true);
        const fd = new FormData();
        fd.append("challenge_id", id);
        fd.append("notes", notes);
        fd.append("video", file);
        if (c.is_weighted && weightKg) fd.append("weight_kg", weightKg);
        if (c.is_weighted && repsDone) fd.append("reps_done", repsDone);
        // ML metadata (browser-side count + form)
        const mlKey = getMlKey(c.exercise_type);
        if (mlKey) {
            fd.append("ml_exercise", mlKey);
            fd.append("ml_count", String(mlReps));
            fd.append("ml_form_score", String(mlFormScore.toFixed(3)));
        }
        try {
            const { data: created } = await client.post("/submissions", fd, {
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Sent for review. No fake progress.");
            navigate(`/app/submissions/${created.id}`);
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (phase === "active") {
        return (
            <div className="fixed inset-0 z-50 bg-charcoal-900 flex flex-col" data-testid="active-challenge-fullscreen">
                {/* Live camera preview as backdrop */}
                <video
                    ref={videoElRef}
                    autoPlay
                    muted
                    playsInline
                    data-testid="active-camera-preview"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: "scaleX(-1)", opacity: cameraReady ? 1 : 0 }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/85" />

                <div className="relative z-10 px-5 pt-10 flex items-center justify-between">
                    <button
                        onClick={() => {
                            stopStream();
                            setRunning(false);
                            setPhase("intro");
                            setSeconds(c.time_limit_seconds);
                            setFile(null);
                        }}
                        className="text-zinc-300 bg-black/40 backdrop-blur w-10 h-10 flex items-center justify-center"
                        data-testid="exit-active"
                    >
                        <ArrowLeft size={22} />
                    </button>
                    <div className="flex items-center gap-2 bg-black/50 backdrop-blur px-3 py-1.5">
                        {recording ? (
                            <>
                                <span className="w-2 h-2 rounded-full bg-neon-orange animate-pulse" style={{ boxShadow: "0 0 8px #FF3B30" }} />
                                <span className="label-cap text-[10px] text-neon-orange" data-testid="recording-indicator">REC</span>
                            </>
                        ) : cameraReady ? (
                            <>
                                <Video size={12} className="text-neon-green" />
                                <span className="label-cap text-[10px] text-neon-green">Camera Ready</span>
                            </>
                        ) : cameraError ? (
                            <>
                                <VideoOff size={12} className="text-zinc-400" />
                                <span className="label-cap text-[10px] text-zinc-400">No Camera</span>
                            </>
                        ) : (
                            <span className="label-cap text-[10px] text-zinc-400">Requesting Camera…</span>
                        )}
                    </div>
                    <div className="w-10" />
                </div>

                <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="label-cap text-xs text-zinc-300 bg-black/40 backdrop-blur px-3 py-1">{c.title}</div>
                    {mlEnabled && (
                        <div
                            className="mt-3 bg-black/55 backdrop-blur border border-neon-green/40 px-4 py-2 flex items-center gap-3"
                            data-testid="ml-counter-overlay"
                        >
                            <div className="flex flex-col items-center">
                                <span className="label-cap text-[9px] text-neon-green">ML Reps</span>
                                <span className="font-display font-black text-3xl tabular-nums neon-green leading-none" data-testid="ml-rep-count">
                                    {mlReps}
                                </span>
                            </div>
                            <div className="w-px h-9 bg-charcoal-500" />
                            <div className="flex flex-col items-start min-w-[100px]">
                                <span className="label-cap text-[9px] text-zinc-400">
                                    {!mlReady ? "Loading..." : mlLastValid ? "Form OK" : "Shallow"}
                                </span>
                                <div className="w-24 h-1.5 bg-charcoal-700 mt-1 overflow-hidden">
                                    <div
                                        className={`h-full transition-all ${mlLastValid ? "bg-neon-green" : "bg-neon-orange"}`}
                                        style={{ width: `${Math.round(mlDepth * 100)}%` }}
                                        data-testid="ml-depth-bar"
                                    />
                                </div>
                                <span className="label-cap text-[9px] text-zinc-500 mt-0.5">
                                    Form {Math.round(mlFormScore * 100)}%
                                </span>
                            </div>
                        </div>
                    )}
                    <div className="mt-4 font-display font-black text-[120px] sm:text-[140px] leading-none tabular-nums" style={{ textShadow: "0 4px 24px rgba(0,0,0,0.8)" }}>
                        <span className={seconds <= 10 ? "neon-orange animate-pulse" : "neon-green"}>
                            {String(Math.floor(seconds / 60)).padStart(2, "0")}:
                            {String(seconds % 60).padStart(2, "0")}
                        </span>
                    </div>
                    <div className="mt-4 bg-black/50 backdrop-blur px-4 py-3">
                        <div className="font-display font-black text-3xl uppercase">
                            {c.reps_required} {c.exercise_type === "plank" ? "sec hold" : "reps"}
                        </div>
                        <div className="text-zinc-300 mt-1 max-w-sm text-sm">{c.instruction}</div>
                    </div>
                    {cameraError && (
                        <div className="mt-4 surface-1 p-3 max-w-sm text-xs text-zinc-400 border-l-2 border-l-neon-orange">
                            Camera unavailable: {cameraError}. You can still complete the challenge and upload a video manually after.
                        </div>
                    )}
                </div>

                <div className="relative z-10 p-5 space-y-3">
                    {seconds === 0 ? (
                        <button
                            onClick={finalizeAndUpload}
                            data-testid="active-done"
                            className="btn-neon w-full py-4 text-lg"
                        >
                            <CheckCircle2 className="inline mr-2" size={20} /> Time's Up — Submit Proof
                        </button>
                    ) : (
                        <>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setRunning(!running)}
                                    data-testid="active-toggle"
                                    disabled={!cameraReady && !cameraError}
                                    className="btn-primary flex-1 py-4 text-lg flex items-center justify-center gap-2 disabled:opacity-40"
                                >
                                    {running ? <Pause size={20} /> : <Play size={20} />}
                                    {running ? "Pause" : "Start & Record"}
                                </button>
                                <button
                                    onClick={() => {
                                        setRunning(false);
                                        setSeconds(c.time_limit_seconds);
                                        if (recorderRef.current && recorderRef.current.state !== "inactive") {
                                            try { recorderRef.current.stop(); } catch {}
                                        }
                                        recorderRef.current = null;
                                        chunksRef.current = [];
                                        setFile(null);
                                    }}
                                    className="px-5 surface-2 hover:border-neon-orange"
                                    data-testid="active-reset"
                                >
                                    <RotateCw size={20} />
                                </button>
                            </div>
                            <button
                                onClick={finalizeAndUpload}
                                className="w-full py-3 text-zinc-300 label-cap text-xs bg-black/40 backdrop-blur"
                                data-testid="active-skip"
                            >
                                Finished early — Submit Proof
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (phase === "upload") {
        return (
            <div className="px-5 pt-8 pb-32">
                <button
                    onClick={() => setPhase("intro")}
                    className="inline-flex items-center text-zinc-500 hover:text-white"
                    data-testid="upload-back"
                >
                    <ArrowLeft size={18} />
                    <span className="ml-2 label-cap text-xs">Back</span>
                </button>

                <div className="mt-6">
                    <span className="label-cap text-xs text-neon-orange">Submit Proof</span>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">
                        Show The Work
                    </h1>
                    <p className="text-zinc-400 text-sm mt-2">
                        Upload your full unedited video. Admins review every submission.
                    </p>
                </div>

                <div className="mt-6 space-y-4">
                    {file && file.name && file.name.startsWith("proof-") && (
                        <div className="surface-2 p-4" data-testid="autorecorded-preview-card">
                            <div className="flex items-center justify-between mb-2">
                                <div className="label-cap text-xs text-neon-green flex items-center gap-1">
                                    <Video size={12} /> Auto-Recorded
                                </div>
                                <span className="text-[10px] text-zinc-500 label-cap">
                                    {(file.size / 1024 / 1024).toFixed(1)} MB
                                </span>
                            </div>
                            <video
                                src={URL.createObjectURL(file)}
                                controls
                                playsInline
                                className="w-full bg-black"
                                style={{ maxHeight: 280 }}
                                data-testid="autorecorded-preview"
                            />
                            <p className="text-xs text-zinc-500 mt-2">
                                Captured during your challenge. Review, then submit — or replace below.
                            </p>
                        </div>
                    )}

                    <label className="block">
                        <input
                            type="file"
                            accept="video/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0])}
                            data-testid="upload-record-input"
                        />
                        <div className="surface-2 p-6 flex flex-col items-center justify-center hover:border-neon-green cursor-pointer transition-colors">
                            <Camera size={28} className="text-neon-green" />
                            <div className="font-display font-bold uppercase text-base mt-2">
                                {file && file.name?.startsWith("proof-") ? "Re-Record" : "Record Now"}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">Open camera</div>
                        </div>
                    </label>

                    <label className="block">
                        <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0])}
                            data-testid="upload-file-input"
                        />
                        <div className="surface-1 p-6 flex flex-col items-center justify-center hover:border-neon-orange cursor-pointer transition-colors">
                            <Upload size={28} className="text-neon-orange" />
                            <div className="font-display font-bold uppercase text-base mt-2">
                                {file && file.name?.startsWith("proof-") ? "Replace With File" : "Upload Video"}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">Pick from device</div>
                        </div>
                    </label>

                    {file && !file.name?.startsWith("proof-") && (
                        <div className="surface-2 p-4">
                            <div className="label-cap text-xs text-neon-green">Selected</div>
                            <div className="text-sm text-white mt-1 break-all" data-testid="upload-filename">
                                {file.name}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">
                                {(file.size / 1024 / 1024).toFixed(1)} MB
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="label-cap text-xs text-zinc-500">Notes (optional)</label>
                        <textarea
                            data-testid="upload-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            placeholder="Anything you want the admin to know..."
                            className="w-full mt-2 bg-charcoal-700 border border-charcoal-500 px-4 py-3 text-white text-sm focus:border-neon-orange focus:outline-none"
                        />
                    </div>

                    {c.is_weighted && (
                        <div className="surface-1 p-4 border-l-2 border-l-neon-orange" data-testid="weighted-pr-block">
                            <div className="label-cap text-xs text-neon-orange">Log For Personal Record (Optional)</div>
                            <p className="text-xs text-zinc-400 mt-1 mb-3">
                                Enter the load and reps performed. On approval, we'll track your estimated 1RM.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="label-cap text-[10px] text-zinc-500">Weight (kg)</label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={0.5}
                                        value={weightKg}
                                        onChange={(e) => setWeightKg(e.target.value)}
                                        placeholder="60"
                                        data-testid="weight-kg-input"
                                        className="w-full mt-1 bg-charcoal-700 border border-charcoal-500 px-3 py-2 text-white text-sm focus:border-neon-orange focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="label-cap text-[10px] text-zinc-500">Reps Done</label>
                                    <input
                                        type="number"
                                        min={1}
                                        value={repsDone}
                                        onChange={(e) => setRepsDone(e.target.value)}
                                        placeholder="8"
                                        data-testid="reps-done-input"
                                        className="w-full mt-1 bg-charcoal-700 border border-charcoal-500 px-3 py-2 text-white text-sm focus:border-neon-orange focus:outline-none"
                                    />
                                </div>
                            </div>
                            {weightKg && repsDone && (
                                <div className="mt-3 text-xs text-zinc-300">
                                    Estimated 1RM:{" "}
                                    <span className="text-neon-green font-display font-black text-base">
                                        {(Number(weightKg) * (1 + Number(repsDone) / 30)).toFixed(1)} kg
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {submitting && (
                        <div className="surface-2 p-3">
                            <div className="label-cap text-xs text-neon-green">Uploading {progress}%</div>
                            <div className="h-1 bg-charcoal-500 mt-2">
                                <div
                                    className="h-full bg-neon-green transition-all"
                                    style={{ width: `${progress}%`, boxShadow: "0 0 8px #39FF14" }}
                                />
                            </div>
                        </div>
                    )}

                    <button
                        onClick={submit}
                        disabled={!file || submitting}
                        data-testid="upload-submit"
                        className="btn-primary w-full py-4 text-lg disabled:opacity-50"
                    >
                        {submitting ? "Sending..." : "Submit For Review"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="px-5 pt-8 pb-32">
            <Link to="/app/challenges" className="inline-flex items-center text-zinc-500 hover:text-white" data-testid="cd-back">
                <ArrowLeft size={18} />
                <span className="ml-2 label-cap text-xs">Back</span>
            </Link>

            <div className="mt-6">
                <div className="flex items-center gap-2">
                    <span className="label-cap text-xs text-neon-green">Level {c.level}</span>
                    <DiffBadge d={c.difficulty} />
                    {c.is_weighted && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 text-neon-orange bg-neon-orange/10 border border-neon-orange/40">
                            Iron · Equipment
                        </span>
                    )}
                </div>
                <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-2 leading-[0.9]">
                    {c.title}
                </h1>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="surface-2 p-4">
                    <Clock size={16} className="text-neon-green" />
                    <div className="font-display font-black text-3xl mt-1">{c.time_limit_seconds}s</div>
                    <div className="label-cap text-[10px] text-zinc-500">Time Limit</div>
                </div>
                <div className="surface-2 p-4">
                    <div className="text-neon-orange font-display font-black text-3xl">+{c.xp_reward}</div>
                    <div className="label-cap text-[10px] text-zinc-500">XP Reward</div>
                </div>
            </div>

            <div className="mt-6 surface-1 p-4">
                <div className="label-cap text-xs text-neon-green">Instruction</div>
                <p className="mt-2 text-zinc-300 leading-relaxed">{c.instruction}</p>
            </div>

            {c.safety_notes && (
                <div className="mt-3 surface-1 p-4 border-l-2 border-l-neon-orange">
                    <div className="label-cap text-xs text-neon-orange flex items-center gap-2">
                        <AlertTriangle size={12} /> Safety Notes
                    </div>
                    <p className="mt-2 text-zinc-400 text-sm">{c.safety_notes}</p>
                </div>
            )}

            {c.is_locked && c.required_items?.length > 0 && (
                <div className="mt-3 surface-1 p-4 border-l-2 border-l-neon-orange" data-testid="challenge-detail-locked">
                    <div className="label-cap text-xs text-neon-orange flex items-center gap-2">
                        <AlertTriangle size={12} /> Locked
                    </div>
                    <p className="mt-2 text-zinc-300 text-sm">
                        This challenge requires:
                    </p>
                    <div className="mt-3 space-y-2">
                        {c.required_items.map((it) => (
                            <Link
                                key={it.id}
                                to="/app/shop"
                                data-testid={`unlock-cta-${it.id}`}
                                className="surface-2 p-3 flex items-center gap-3 hover:border-neon-green transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold truncate">{it.name}</div>
                                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                                        {it.category} · {it.price_noex} $NoEx
                                    </div>
                                </div>
                                <span className="label-cap text-[10px] text-neon-green">Buy →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {sub && (
                <div className="mt-6 surface-2 p-4">
                    <div className="label-cap text-xs text-zinc-500">Latest Attempt</div>
                    <div className="flex items-center justify-between mt-2">
                        <span className="font-display font-bold uppercase">{sub.status}</span>
                        <Link to={`/app/submissions/${sub.id}`} className="text-neon-green label-cap text-xs">
                            View
                        </Link>
                    </div>
                </div>
            )}

            <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-[480px] px-5">
                <button
                    onClick={() => !c.is_locked && setPhase("active")}
                    disabled={c.is_locked}
                    data-testid="start-challenge-button"
                    className="btn-neon w-full py-4 text-lg shadow-2xl disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {c.is_locked ? "Locked — Buy Gear To Unlock" : "Start The Challenge"}
                </button>
            </div>
        </div>
    );
}

function DiffBadge({ d }) {
    const map = {
        easy: { c: "#39FF14", l: "Easy" },
        medium: { c: "#F59E0B", l: "Medium" },
        hard: { c: "#FF3B30", l: "Hard" },
        elite: { c: "#FF2424", l: "Elite" },
    };
    const x = map[d] || map.easy;
    return (
        <span
            className="text-[9px] uppercase tracking-widest font-bold px-2 py-1"
            style={{ color: x.c, border: `1px solid ${x.c}40`, background: `${x.c}10` }}
        >
            {x.l}
        </span>
    );
}
